import { useState } from "react";
import { useSelector } from "react-redux";
import { getPrediction } from "@b2bportal/core-flights";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { ITripTerminus, SortOptionEnum } from "@b2bportal/core-types";
import { TripCategory } from "@overrides/types";
import { FlightListGridFilter, FlightShopTitle, Sort } from "@tenant/flights";
import { PricePredictionCard } from "../PricePredictionCard";
import styles from "./DeparturePageHeader.module.scss";
import { GridV2PricingComparisonDialog } from "@/components/shared/GridV2PricingComparisonDialog";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import {
  FixedPricingTab,
  HowPricingWorksLink,
  FlightShopHeaderBanner,
  GridV2RefreshedBanner,
} from "@/modules/flights/components";
// Direct files, not the module barrel: the barrel pulls the opt-out thunk and
// tracking into this header's import graph and creates an init cycle through
// @hopper-b2b/checkout (same reason the store imports the slice directly).
import { GridV2Gate } from "@/modules/grid-v2/GridV2Gate";
import { GridV2OptOut } from "@/modules/grid-v2/GridV2OptOut";
import { useGridV2 } from "@/modules/grid-v2/useGridV2";
import type { PricingMode } from "@/store/flights/shop/store/slice";

interface DeparturePageHeaderProps {
  tripCategory: TripCategory;
  destination: ITripTerminus;
  isError: boolean;
  isFixedPriceRoute: boolean;
  pricingMode: PricingMode;
  flightListLength: number;
  sortOption: SortOptionEnum;
  handleSortChange: (value: SortOptionEnum) => void;
  setPricingMode: (value: PricingMode) => void;
}

export const DeparturePageHeader = ({
  tripCategory,
  destination,
  isError,
  isFixedPriceRoute,
  pricingMode,
  flightListLength,
  sortOption,
  handleSortChange,
  setPricingMode,
}: DeparturePageHeaderProps) => {
  const [, cn] = useModuleBEM(styles, "DeparturePageHeader");

  const { isPricedOnGridV2 } = useGridV2();
  const [isOptOutOpen, setIsOptOutOpen] = useState(false);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);

  const { isExperimentVariant } = useExperiment();
  const isPricePredictionEnabled = isExperimentVariant(
    Experiments.RBC_ENABLE_PRICE_PREDICTION.name,
    Experiments.RBC_ENABLE_PRICE_PREDICTION.variants.available
  );

  const isGridPricePredictionEnabled = isExperimentVariant(
    Experiments.GRID_PRICE_PREDICTION_ENABLED.name,
    Experiments.GRID_PRICE_PREDICTION_ENABLED.variants.available
  );

  // The card renders nothing without a prediction; its wrapper is a grid row
  // and must go with it, or it leaves a blank gap above the banners.
  const hasPrediction = Boolean(useSelector(getPrediction)?.dealness);

  const hasFlights = flightListLength !== 0;
  const isFlexPricingMode = pricingMode !== "fixed";
  const isMultiCity = tripCategory === TripCategory.MULTI_CITY;
  const shouldShowPrediction =
    hasPrediction &&
    hasFlights &&
    isPricePredictionEnabled &&
    !isMultiCity &&
    (!isFixedPriceRoute || (isFlexPricingMode && isGridPricePredictionEnabled));

  return (
    <div className={cn("background-container")}>
      <FlightShopTitle
        className={cn("title-container", { isFixedPriceRoute })}
        tripCategory={tripCategory}
        destination={destination}
      />
      {!isError && (
        <>
          {isFixedPriceRoute && (
            <div className={cn("pricing-tab-wrapper")}>
              <FixedPricingTab
                className={cn("pricing-tab")}
                onValueChange={setPricingMode}
                defaultValue={pricingMode}
                isGridV2={isPricedOnGridV2}
              />
              <HowPricingWorksLink className={cn("how-pricing-works")} />
            </div>
          )}
          {shouldShowPrediction && (
            <div className={cn("flex-pricing-section")}>
              <div className={cn("prediction")}>
                <PricePredictionCard />
              </div>
            </div>
          )}
          <FlightShopHeaderBanner
            isFixedPriceRoute={isFixedPriceRoute}
            pricingMode={pricingMode}
            flightListLength={flightListLength}
            className={cn("points-eligibility-banner")}
          />
        </>
      )}

      <div className={cn("sort")}>
        <Sort value={sortOption} handleSortChange={handleSortChange} />
      </div>

      <FlightListGridFilter className={cn("grid-filter")} />

      {/* Last in the DOM on purpose: the grid auto-places it under the
          sort/filter row, right above the flight list (Figma 40629:108547). */}
      {!isError && isFixedPriceRoute && pricingMode === "fixed" && (
        <GridV2Gate>
          <GridV2RefreshedBanner
            className={cn("grid-v2-refreshed-banner")}
            onLearnWhatsNew={() => setIsComparisonOpen(true)}
            onUseOriginalPricing={() => setIsOptOutOpen(true)}
          />
        </GridV2Gate>
      )}
      {/* Outside the gate and the tab guard on purpose: the opt-out flips the
          gate to V1 while the dialog is still open, and a tab switch must not
          unmount it either; both would skip onClose and leak the open state. */}
      <GridV2OptOut
        open={isOptOutOpen}
        onClose={() => setIsOptOutOpen(false)}
      />
      {/* Opened from the banner, so "back" has no opener to return to. */}
      <GridV2PricingComparisonDialog
        open={isComparisonOpen}
        onClose={() => setIsComparisonOpen(false)}
        onBack={() => setIsComparisonOpen(false)}
      />
    </div>
  );
};
